/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { FunctionComponent, useEffect, useState, ReactNode } from 'react';
import SubTitle from '../SubTitle/SubTitle';
import Icon, { Color } from '../../DataDisplay/Icon/Icon';
import Box from '../../Layout/Box/Box';
import styles from './Accordion.module.scss';
import { useDatadogLog } from '../../Input/logging';

export type AccordionArgs = {
  isOpen: boolean;
  onClick: () => void;
};

export type AccordionProps = {
  title: string | React.JSX.Element;
  titleColor?: string;
  subBody?: React.JSX.Element;
  body: React.JSX.Element;
  open?: boolean;
  fontSize?: string;
  isArrowClickable?: boolean;
  arrowPosition?: 'top' | 'bottom' | 'box';
  variant?: 'normal' | 'shadow' | 'box' | 'card';
  iconSize?: 'xsm' | 'sm' | 'md' | 'lg' | 'xlg' | 'xxlg' | 'jumbo' | 'xl-jumbo' | 'logo';
  iconColor?: Color;
  isDisabled?: boolean;
  angleSize?: string;
  angleText?: string | React.JSX.Element;
  angleColor?: 'black' | 'blue';
  titlePadding?: string;
  bodyPadding?: string;
  fontWeight?: string;
  style?: {
    '--borderRadius'?: string;
    '--boxShadow'?: string;
    '--padding'?: string;
    '--background'?: string;
  };
  onClick?: () => void;
  renderRightSection?: (args: AccordionArgs) => ReactNode;
};

type FC = FunctionComponent<AccordionProps>;

const Accordion: FC = ({
  title,
  titleColor = '',
  subBody,
  body,
  open = false,

  isArrowClickable = false,
  iconSize = 'md',
  iconColor = 'primary-dark',
  variant = 'normal',
  fontSize = variant === 'card' ? '25px' : undefined,
  fontWeight = variant === 'card' ? '600' : undefined,
  arrowPosition,
  isDisabled = false,
  angleSize,
  angleText,
  angleColor = 'black',
  titlePadding,
  bodyPadding,
  style,
  onClick,
  renderRightSection,
}: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(open);
  const [position, setPosition] = useState(arrowPosition);
  const log = useDatadogLog();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  const clickHandler = () => {
    log({
      message: `${!isOpen ? 'Opened' : 'Closed'} ${String(title)}`,
      payload: {
        name: title,
        value: !isOpen,
      },
      actionType: 'Button',
    });
    onClick ? onClick() : toggleMenu();
    return false;
  };

  useEffect(() => {
    setIsOpen(open); // Sync state with open prop
  }, [open]);

  useEffect(() => {
    setPosition(isOpen ? 'bottom' : 'top'); // Update position based on isOpen state
  }, [isOpen]);

  return (
    <div className={[styles.Container, styles[variant]].join(' ')} style={style}>
      <button
        type="button"
        className={styles.headerButton}
        style={{ padding: titlePadding }}
        onClick={!isArrowClickable && !isDisabled ? clickHandler : undefined}
      >
        <Box jc="sb" ai="center" height="auto">
          <SubTitle subTitle={title} color={titleColor} fontSize={fontSize} fontWeight={fontWeight} />
          {renderRightSection ? (
            <div className={styles.rightSectionContainer}>{renderRightSection({ isOpen, onClick: clickHandler })}</div>
          ) : (
            <Box>
              {angleText}
              {!isDisabled && (
                <div
                  className={[styles.arrowContainer, styles[getArrowPositionClass()]].join(' ')}
                  onClick={isArrowClickable ? clickHandler : undefined}
                >
                  <Icon
                    icon="angleDown"
                    width={angleSize}
                    color={angleColor === 'blue' ? 'blue' : iconColor}
                    size={iconSize}
                  />
                </div>
              )}
            </Box>
          )}
        </Box>
      </button>

      <div className={[styles.ContainerBody, styles[getOpenStyle()]].join(' ')}>
        <div className={styles.bodyWrapper}>
          <div className={styles.body} style={{ padding: bodyPadding }}>
            {body}
          </div>
        </div>
      </div>

      {subBody && subBody}
    </div>
  );
  function getOpenStyle() {
    return isOpen ? 'open' : '';
  }

  function getArrowPositionClass() {
    return position === 'top' ? 'arrowBottom' : position === 'bottom' ? 'arrowTop' : '';
  }
};

export default Accordion;
